import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const TOKEN_KEY = "axiom_ai_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, object=auth
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      api.defaults.headers.common.Authorization = `Bearer ${t}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete api.defaults.headers.common.Authorization;
    }
    setToken(t);
  }, []);

  // On mount, check session
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored) {
        api.defaults.headers.common.Authorization = `Bearer ${stored}`;
      }
      try {
        const { data } = await api.get("/auth/me", { withCredentials: true });
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) {
          applyToken(null);
          setUser(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyToken]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post(
        "/auth/login",
        { email, password },
        { withCredentials: true }
      );
      applyToken(data.token);
      setUser(data.user);
      return data.user;
    },
    [applyToken]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {}, { withCredentials: true });
    } catch {
      // ignore
    }
    applyToken(null);
    setUser(false);
  }, [applyToken]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
